import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Users, Mail, Calendar, LogOut, Trash2, ExternalLink, RefreshCw } from "lucide-react";

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="p-5 rounded-xl bg-white border border-slate-200" data-testid={testid}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-md bg-blue-50 flex items-center justify-center"><Icon className="w-4 h-4 text-[#2563EB]" /></div>
        <div className="text-[12px] text-slate-500 uppercase tracking-wider font-semibold">{label}</div>
      </div>
      <div className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit" }}>{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState({ subscribers: 0, contacts: 0, contacts_new: 0, consultations: 0, consultations_pending: 0 });
  const [subs, setSubs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [consults, setConsults] = useState([]);

  const refresh = async () => {
    try {
      const [s, sb, ct, cs] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/subscribers"),
        api.get("/admin/contacts"),
        api.get("/admin/consultations"),
      ]);
      setStats(s.data); setSubs(sb.data); setContacts(ct.data); setConsults(cs.data);
    } catch { toast.error("Failed to load data"); }
  };

  useEffect(() => { if (user && user.role === "admin") refresh(); }, [user]);

  if (loading) return <div className="p-10 text-slate-500">Loading...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  const removeSub = async (email) => {
    if (!confirm(`Remove ${email}?`)) return;
    await api.delete(`/admin/subscribers/${encodeURIComponent(email)}`);
    toast.success("Removed"); refresh();
  };
  const removeContact = async (id) => {
    if (!confirm("Delete this message?")) return;
    await api.delete(`/admin/contacts/${id}`);
    toast.success("Deleted"); refresh();
  };
  const markRead = async (id) => {
    await api.patch(`/admin/contacts/${id}`, { status: "read" });
    refresh();
  };
  const markConsulted = async (id) => {
    await api.patch(`/admin/consultations/${id}`, { status: "contacted" });
    refresh();
  };
  const removeConsult = async (id) => {
    if (!confirm("Delete this booking?")) return;
    await api.delete(`/admin/consultations/${id}`);
    toast.success("Deleted"); refresh();
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Sidebar-less top-bar layout */}
      <div className="bg-[#0A2540] text-white">
        <div className="container mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-[#2563EB] hex-clip flex items-center justify-center relative">
              <div className="w-9 h-9 scale-[0.7] bg-[#0A2540] hex-clip absolute inset-0 m-auto" />
              <span className="relative text-white font-extrabold z-10" style={{ fontFamily: "Outfit" }}>E</span>
            </div>
            <div>
              <div className="font-extrabold" style={{ fontFamily: "Outfit" }}>EASYWORKS</div>
              <div className="text-[9px] tracking-[0.2em] text-slate-400 font-semibold">ADMIN PANEL</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={refresh} className="text-white hover:bg-white/10 hover:text-white gap-2" data-testid="refresh-btn"><RefreshCw className="w-4 h-4" />Refresh</Button>
            <Link to="/"><Button variant="ghost" className="text-white hover:bg-white/10 hover:text-white gap-2"><ExternalLink className="w-4 h-4" />View site</Button></Link>
            <Button onClick={doLogout} className="bg-[#2563EB] hover:bg-[#1D4ED8] gap-2" data-testid="admin-logout-btn"><LogOut className="w-4 h-4" />Logout</Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-7xl px-6 py-8">
        <div className="mb-2 text-[13px] text-slate-500">Signed in as <strong>{user.email}</strong></div>
        <h1 className="text-3xl font-bold text-slate-900 mb-8" style={{ fontFamily: "Outfit" }}>Dashboard</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Mail} label="Subscribers" value={stats.subscribers} testid="stat-subs" />
          <StatCard icon={Users} label="Contact Messages" value={stats.contacts} testid="stat-contacts" />
          <StatCard icon={Calendar} label="Consultations" value={stats.consultations} testid="stat-consults" />
          <StatCard icon={RefreshCw} label="Pending Actions" value={stats.contacts_new + stats.consultations_pending} testid="stat-pending" />
        </div>

        <Tabs defaultValue="contacts" className="w-full">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contact Messages ({contacts.length})</TabsTrigger>
            <TabsTrigger value="consults" data-testid="tab-consults">Consultations ({consults.length})</TabsTrigger>
            <TabsTrigger value="subs" data-testid="tab-subs">Subscribers ({subs.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="contacts" className="mt-4">
            <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left"><tr>
                  <th className="p-3 font-semibold">Name</th><th className="p-3 font-semibold">Email</th><th className="p-3 font-semibold">Subject</th><th className="p-3 font-semibold">Status</th><th className="p-3 font-semibold">Actions</th>
                </tr></thead>
                <tbody>
                  {contacts.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-400">No messages yet.</td></tr>}
                  {contacts.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100" data-testid={`contact-row-${c.id}`}>
                      <td className="p-3 font-medium text-slate-900">{c.name}</td>
                      <td className="p-3 text-slate-600">{c.email}</td>
                      <td className="p-3 text-slate-600">{c.subject || "-"}</td>
                      <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${c.status === "new" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}>{c.status}</span></td>
                      <td className="p-3 flex gap-2">
                        {c.status === "new" && <Button size="sm" variant="outline" onClick={() => markRead(c.id)}>Mark read</Button>}
                        <Button size="sm" variant="ghost" onClick={() => removeContact(c.id)} className="text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="consults" className="mt-4">
            <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left"><tr>
                  <th className="p-3 font-semibold">Name</th><th className="p-3 font-semibold">Contact</th><th className="p-3 font-semibold">Company</th><th className="p-3 font-semibold">Interest</th><th className="p-3 font-semibold">Status</th><th className="p-3 font-semibold">Actions</th>
                </tr></thead>
                <tbody>
                  {consults.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400">No consultations yet.</td></tr>}
                  {consults.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100" data-testid={`consult-row-${c.id}`}>
                      <td className="p-3 font-medium text-slate-900">{c.name}</td>
                      <td className="p-3 text-slate-600">{c.email}<br /><span className="text-[12px] text-slate-400">{c.phone}</span></td>
                      <td className="p-3 text-slate-600">{c.company || "-"}</td>
                      <td className="p-3 text-slate-600">{c.service_interest || "-"}</td>
                      <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${c.status === "pending" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{c.status}</span></td>
                      <td className="p-3 flex gap-2">
                        {c.status === "pending" && <Button size="sm" variant="outline" onClick={() => markConsulted(c.id)}>Mark contacted</Button>}
                        <Button size="sm" variant="ghost" onClick={() => removeConsult(c.id)} className="text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="subs" className="mt-4">
            <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left"><tr>
                  <th className="p-3 font-semibold">Email</th><th className="p-3 font-semibold">Subscribed On</th><th className="p-3 font-semibold text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {subs.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-slate-400">No subscribers yet.</td></tr>}
                  {subs.map((s) => (
                    <tr key={s.email} className="border-t border-slate-100" data-testid={`sub-row-${s.email}`}>
                      <td className="p-3 font-medium text-slate-900">{s.email}</td>
                      <td className="p-3 text-slate-600">{new Date(s.created_at).toLocaleString()}</td>
                      <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => removeSub(s.email)} className="text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
